/**
 * Google Drive Sync Hook
 * Manages sync state and operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { googleDriveService, GoogleUser } from '../services/googleDrive';
import { backupService } from '../services/backupService';
import { backendLogger } from '../services/backendLogger';
import { persistence } from '../services/persistence';

export interface GoogleDriveSyncState {
  isAuthenticated: boolean;
  currentUser: GoogleUser | null;
  isSyncing: boolean;
  lastSyncTime: string | null;
  error: string | null;
  autoSyncEnabled: boolean;
}

export interface GoogleDriveSyncActions {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  syncNow: () => Promise<void>;
  setAutoSync: (enabled: boolean) => void;
  downloadAndRestore: () => Promise<void>;
  deleteCloudBackup: () => Promise<void>;
}

const AUTO_SYNC_KEY = 'google-drive-auto-sync';
const LAST_SYNC_KEY = 'google-drive-last-sync';
const DEBOUNCE_DELAY = 30000; // 30 seconds

export function useGoogleDriveSync(): GoogleDriveSyncState & GoogleDriveSyncActions {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<GoogleUser | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoSyncEnabled, setAutoSyncEnabledState] = useState(() => {
    return persistence.getItem(AUTO_SYNC_KEY) === 'true';
  });

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  /**
   * Perform sync operation
   */
  const performSync = useCallback(async () => {
    try {
      if (!googleDriveService.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      console.log('[GoogleDriveSync] Starting sync...');

      // Create local backup
      console.log('[GoogleDriveSync] Creating local backup snapshot...');
      const localBackup = await backupService.createBackupSnapshot();
      console.log('[GoogleDriveSync] Local backup created:', {
        version: localBackup.version,
        syncedAt: localBackup.syncedAt,
        dataKeys: Object.keys(localBackup.data).length,
      });

      // Download cloud backup
      console.log('[GoogleDriveSync] Downloading cloud backup...');
      const cloudBackup = await googleDriveService.downloadBackup();

      if (cloudBackup) {
        console.log('[GoogleDriveSync] Cloud backup found, merging...');
        
        // Validate cloud backup
        const isValid = await backupService.validateBackup(cloudBackup);
        if (!isValid) {
          throw new Error('Cloud backup is invalid or corrupted');
        }

        // Merge backups
        console.log('[GoogleDriveSync] Merging backups...');
        const mergedBackup = backupService.mergeBackups(localBackup, cloudBackup);
        
        // Restore merged data locally
        console.log('[GoogleDriveSync] Restoring merged data locally...');
        backupService.restoreAllLocalStorageData(mergedBackup);
        
        // Upload merged backup to cloud
        console.log('[GoogleDriveSync] Uploading merged backup to cloud...');
        await googleDriveService.uploadBackup(mergedBackup);
        console.log('[GoogleDriveSync] Merged backup uploaded successfully');
        
        const syncTime = new Date().toISOString();
        setLastSyncTime(syncTime);
        persistence.setItem(LAST_SYNC_KEY, syncTime);
        
        console.log('[GoogleDriveSync] Sync completed with merge, reloading page...');
        
        // Reload page to reflect any restored data
        window.location.reload();
      } else {
        console.log('[GoogleDriveSync] No cloud backup found, uploading local backup...');
        
        // No cloud backup, just upload local
        await googleDriveService.uploadBackup(localBackup);
        console.log('[GoogleDriveSync] Initial backup uploaded successfully');
        
        const syncTime = new Date().toISOString();
        setLastSyncTime(syncTime);
        persistence.setItem(LAST_SYNC_KEY, syncTime);
        
        console.log('[GoogleDriveSync] Initial sync completed');
      }
    } catch (error: any) {
      const errorDetails = {
        message: error.message,
        stack: error.stack,
        name: error.name,
        error: error,
      };
      console.error('[GoogleDriveSync] Sync failed:', errorDetails);
      backendLogger.error('Google Drive sync failed', error, {
        function: 'performSync',
        step: 'sync',
      });
      throw error; // Re-throw to be caught by caller
    }
  }, []);

  /**
   * Manual sync trigger
   */
  const syncNow = useCallback(async () => {
    try {
      setError(null);
      setIsSyncing(true);
      await performSync();
      setIsSyncing(false);
    } catch (err: any) {
      console.error('[GoogleDriveSync] Sync error:', err);
      setError(err.message || 'Sync failed');
      setIsSyncing(false);
      throw err;
    }
  }, [performSync]);

  /**
   * Initialize Google auth and check authentication status
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        await googleDriveService.initGoogleAuth();
        const authenticated = googleDriveService.isAuthenticated();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const user = await googleDriveService.getCurrentUser();
          setCurrentUser(user);

          // Load last sync time
          const savedLastSync = persistence.getItem(LAST_SYNC_KEY);
          setLastSyncTime(savedLastSync);

          // Auto-sync on load if enabled
          if (autoSyncEnabled && !isInitializedRef.current) {
            isInitializedRef.current = true;
            syncNow().catch((err) => {
              console.error('[GoogleDriveSync] Auto-sync on init failed:', err);
            });
          }
        }
      } catch (err) {
        console.error('[GoogleDriveSync] Initialization error:', err);
        setError('Failed to initialize Google Drive');
      }
    };

    initialize();
  }, [autoSyncEnabled, syncNow]);

  /**
   * Sign in with Google
   */
  const signIn = useCallback(async () => {
    try {
      setError(null);
      setIsSyncing(true);
      
      console.log('[GoogleDriveSync] Starting sign-in process...');
      
      // Ensure Google auth is initialized before signing in
      await googleDriveService.initGoogleAuth();
      console.log('[GoogleDriveSync] Google auth initialized');
      
      await googleDriveService.signInWithGoogle();
      console.log('[GoogleDriveSync] Google sign-in successful');
      setIsAuthenticated(true);

      const user = await googleDriveService.getCurrentUser();
      setCurrentUser(user);
      console.log('[GoogleDriveSync] User info retrieved:', user?.email);

      // Perform initial sync after sign-in
      console.log('[GoogleDriveSync] Starting initial sync...');
      await performSync();
      console.log('[GoogleDriveSync] Initial sync completed successfully');
      
      setIsSyncing(false);
    } catch (err: any) {
      const errorDetails = {
        message: err.message,
        stack: err.stack,
        name: err.name,
        error: err,
      };
      console.error('[GoogleDriveSync] Sign in error:', errorDetails);
      backendLogger.error('Google Drive sign-in failed', err, {
        function: 'signIn',
        step: 'sign-in',
      });
      setError(err.message || 'Failed to sign in with Google');
      setIsSyncing(false);
      setIsAuthenticated(false); // Reset auth state on error
    }
  }, [performSync]);

  /**
   * Sign out from Google
   */
  const signOut = useCallback(async () => {
    try {
      await googleDriveService.signOutFromGoogle();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setLastSyncTime(null);
      setError(null);
      persistence.removeItem(LAST_SYNC_KEY);
      persistence.removeItem(AUTO_SYNC_KEY);
      
      // Clear any pending sync timeouts
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      
      // Signed out successfully
    } catch (error: any) {
      console.error('[GoogleDriveSync] Sign out error:', error);
      // Still clear local state even if revoke fails
      setIsAuthenticated(false);
      setCurrentUser(null);
      setLastSyncTime(null);
      setError(null);
      persistence.removeItem(LAST_SYNC_KEY);
      persistence.removeItem(AUTO_SYNC_KEY);
    }
  }, []);

  /**
   * Download and restore backup (without uploading)
   */
  const downloadAndRestore = useCallback(async () => {
    try {
      setError(null);
      setIsSyncing(true);

      const cloudBackup = await googleDriveService.downloadBackup();
      if (!cloudBackup) {
        throw new Error('No backup found in Google Drive');
      }

      const isValid = await backupService.validateBackup(cloudBackup);
      if (!isValid) {
        throw new Error('Cloud backup is invalid or corrupted');
      }

      backupService.restoreAllLocalStorageData(cloudBackup);
      
      const syncTime = new Date().toISOString();
      setLastSyncTime(syncTime);
      persistence.setItem(LAST_SYNC_KEY, syncTime);

      setIsSyncing(false);

      // Reload to reflect restored data
      window.location.reload();
    } catch (err: any) {
      console.error('[GoogleDriveSync] Restore error:', err);
      setError(err.message || 'Failed to restore from backup');
      setIsSyncing(false);
      throw err;
    }
  }, []);

  /**
   * Delete cloud backup
   */
  const deleteCloudBackup = useCallback(async () => {
    try {
      setError(null);
      setIsSyncing(true);
      await googleDriveService.deleteBackup();
      setLastSyncTime(null);
      persistence.removeItem(LAST_SYNC_KEY);
      setIsSyncing(false);
    } catch (err: any) {
      console.error('[GoogleDriveSync] Delete error:', err);
      setError(err.message || 'Failed to delete backup');
      setIsSyncing(false);
      throw err;
    }
  }, []);

  /**
   * Set auto-sync enabled/disabled
   */
  const setAutoSync = useCallback((enabled: boolean) => {
    setAutoSyncEnabledState(enabled);
    persistence.setItem(AUTO_SYNC_KEY, enabled.toString());
  }, []);

  /**
   * Auto-sync on localStorage changes (debounced)
   */
  useEffect(() => {
    if (!autoSyncEnabled || !isAuthenticated) {
      return;
    }

    const handleStorageChange = () => {
      // Clear existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // Set new timeout for debounced sync
      syncTimeoutRef.current = setTimeout(() => {
        // Auto-syncing after changes
        syncNow().catch((err) => {
          console.error('[GoogleDriveSync] Auto-sync failed:', err);
        });
      }, DEBOUNCE_DELAY);
    };

    // Listen to storage events (from other tabs)
    window.addEventListener('storage', handleStorageChange);

    // Also listen to custom events for same-tab changes
    window.addEventListener('localStorageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [autoSyncEnabled, isAuthenticated, syncNow]);

  return {
    isAuthenticated,
    currentUser,
    isSyncing,
    lastSyncTime,
    error,
    autoSyncEnabled,
    signIn,
    signOut,
    syncNow,
    setAutoSync,
    downloadAndRestore,
    deleteCloudBackup,
  };
}
